/**
 * Cliente da API do INTTRIX HUB.
 *
 * CP11 (sessão BFF, ADR-004 §4): a sessão é o cookie `__Host-inttrix_sessao`,
 * HttpOnly — o navegador o envia sozinho e este código nunca o lê. Nenhum
 * token fica em localStorage ou sessionStorage. O token CSRF, entregue por
 * /api/auth/eu (e renovado no cabeçalho X-CSRF-Token quando a sessão é
 * rotacionada), fica SÓ nesta variável em memória e acompanha as mutações.
 */
export class ErroApi extends Error {
  status: number;
  codigo: string;
  detalhes?: unknown;
  constructor(status: number, codigo: string, mensagem: string, detalhes?: unknown) {
    super(mensagem);
    this.status = status;
    this.codigo = codigo;
    this.detalhes = detalhes;
  }
}

type OuvinteSessao = (motivo: string) => void;
let aoPerderSessao: OuvinteSessao = () => undefined;

export function definirOuvinteSessao(fn: OuvinteSessao): void {
  aoPerderSessao = fn;
}

/**
 * Pacote 5: avisado a cada resposta autenticada de uma requisição NORMAL (que
 * renovou a inatividade no servidor). As de segundo plano não avisam.
 */
let aoUsarServidor: () => void = () => undefined;
export function definirOuvinteUso(fn: () => void): void {
  aoUsarServidor = fn;
}

/**
 * Pacote 5: requisição de segundo plano (atualizações automáticas, conferências
 * da sessão) não conta como uso — o servidor não renova a inatividade. É o
 * cabeçalho `X-Atividade`, independente do `X-Origem` (que segue só para a auditoria).
 */
export const CABECALHO_SEGUNDO_PLANO = { 'X-Atividade': 'segundo-plano' } as const;

/** Token CSRF da sessão atual — somente em memória. */
let csrf: string | null = null;

export function definirCsrf(token: string | null): void {
  csrf = token;
}

/** Cabeçalhos das requisições da aplicação (CSRF nas mutações). */
export function cabecalhosApi(metodo = 'GET'): Record<string, string> {
  const cabecalhos: Record<string, string> = { 'X-Origem': 'aplicacao_web' };
  if (metodo !== 'GET' && csrf) cabecalhos['X-CSRF-Token'] = csrf;
  return cabecalhos;
}

/** Guarda o novo token CSRF quando o servidor rotaciona a sessão. */
export function lerCsrfDaResposta(res: Response): void {
  const novo = res.headers.get('X-CSRF-Token');
  if (novo) csrf = novo;
}

const ROTAS_SEM_SESSAO = ['/api/auth/login', '/api/auth/mfa/verificar', '/api/auth/mfa/pendente', '/api/auth/opcoes'];

export interface OpcoesChamada {
  /** 401 não derruba a sessão na tela (ex.: a primeira consulta, antes do login). */
  silencioso?: boolean;
  /** Pacote 5: requisição de segundo plano — autenticada, mas não renova a inatividade (`X-Atividade: segundo-plano`). */
  fundo?: boolean;
}

export async function chamar<T = any>(metodo: string, caminho: string, corpo?: unknown, opcoes: OpcoesChamada = {}): Promise<T> {
  const cabecalhos = cabecalhosApi(metodo);
  if (opcoes.fundo) Object.assign(cabecalhos, CABECALHO_SEGUNDO_PLANO);
  if (corpo !== undefined) cabecalhos['Content-Type'] = 'application/json';
  const res = await fetch(caminho, {
    method: metodo,
    headers: cabecalhos,
    credentials: 'same-origin',
    body: corpo === undefined ? undefined : JSON.stringify(corpo),
  });
  lerCsrfDaResposta(res);
  const usouServidor = () => !opcoes.fundo && res.ok && !ROTAS_SEM_SESSAO.some((r) => caminho.startsWith(r)) && aoUsarServidor();
  if (res.status === 204) {
    usouServidor();
    return undefined as T;
  }
  const texto = await res.text();
  const dados = texto ? JSON.parse(texto) : undefined;
  if (!res.ok) {
    const erro = new ErroApi(res.status, dados?.codigo ?? 'erro', dados?.mensagem ?? `Erro ${res.status}`, dados?.detalhes);
    if (res.status === 401 && !opcoes.silencioso && !ROTAS_SEM_SESSAO.some((r) => caminho.startsWith(r))) {
      csrf = null;
      aoPerderSessao(erro.message);
    }
    throw erro;
  }
  usouServidor();
  return dados as T;
}

export const api = {
  get: <T = any>(c: string) => chamar<T>('GET', c),
  post: <T = any>(c: string, corpo: unknown = {}) => chamar<T>('POST', c, corpo),
  patch: <T = any>(c: string, corpo: unknown) => chamar<T>('PATCH', c, corpo),
  put: <T = any>(c: string, corpo: unknown) => chamar<T>('PUT', c, corpo),
};

export function consulta(parametros: Record<string, string | number | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(parametros)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}
